import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  SimpleGrid,
  useColorMode,
  Avatar,
  List,
  ListItem,
  ListIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Button,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { brandConfig, getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

interface Leader {
  name: string;
  role: string;
  organisation: string;
  relevance: string;
  keyPositions: string[];
  initials: string;
}

const leaders: Leader[] = [
  {
    name: "Chris Bowen",
    role: "Minister for Climate Change and Energy",
    organisation: "Australian Government",
    relevance: "Controls fuel security policy, SPR decisions",
    keyPositions: [
      "Responsible for strategic petroleum reserve policy",
      "Manages Liquid Fuels Gateway reporting",
      "Key contact for IEA compliance on 90-day reserve obligation",
    ],
    initials: "CB",
  },
  {
    name: "Meg O'Neill",
    role: "CEO",
    organisation: "Woodside Energy",
    relevance: "Largest Australian oil & gas producer",
    keyPositions: [
      "Runs Australia's largest energy company by market cap",
      "Controls significant LNG export contracts to Japan",
      "Key voice on domestic production vs export balance",
    ],
    initials: "MO",
  },
  {
    name: "Frank Calabria",
    role: "CEO",
    organisation: "Origin Energy",
    relevance: "Major fuel importer/retailer",
    keyPositions: [
      "Major diesel and petrol importer into Australia",
      "Operates retail fuel network",
      "Direct relationship with Singapore and Asian refineries",
    ],
    initials: "FC",
  },
  {
    name: "Andrew Forrest",
    role: "Chairman",
    organisation: "Fortescue",
    relevance: "Pushing hydrogen as alternative, politically influential",
    keyPositions: [
      "Advocating green hydrogen as long-term fuel alternative",
      "Politically connected across both parties",
      "Major investor in renewable energy transition",
    ],
    initials: "AF",
  },
  {
    name: "NOPSEMA",
    role: "Offshore Petroleum Safety Regulator",
    organisation: "Australian Government",
    relevance: "Governs offshore production approvals",
    keyPositions: [
      "Approves or blocks offshore drilling permits",
      "Regulates safety of domestic oil production",
      "Key gatekeeper for any increase in domestic supply",
    ],
    initials: "NP",
  },
  {
    name: "Angus Taylor",
    role: "Opposition Energy Spokesperson",
    organisation: "Liberal Party",
    relevance: "Former resources minister, critical voice",
    keyPositions: [
      "Former Energy & Emissions Reduction Minister",
      "Advocates for increased domestic fuel production",
      "Vocal critic of current reserve levels",
    ],
    initials: "AT",
  },
];

const AustraliaDecisionMakers: React.FC = () => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const textPrimary = getColor("text.primary", colorMode);
  const surfaceBg = brandConfig.colors.background.surface;
  const borderLight = brandConfig.colors.border.light;
  const textSecondary = brandConfig.colors.text.secondary;

  return (
    <Box bg={bgMain} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={10} flex="1">
        <VStack spacing={6} align="stretch">
          <Breadcrumb fontSize="sm" color={textSecondary}>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to="/energy-research">
                Energy Research
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink>Australia's Decision Makers</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Heading size="lg" color="#dc2626">
            Australia's Key Energy Decision Makers
          </Heading>
          <Text color={textSecondary} fontSize="sm">
            The people who control Australia's fuel security policy, production, and imports
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            {leaders.map((leader) => (
              <Box
                key={leader.name}
                p={6}
                bg={surfaceBg}
                borderRadius="lg"
                border="1px solid"
                borderColor={borderLight}
                _hover={{
                  borderColor: "#dc2626",
                  shadow: "md",
                  transition: "all 0.2s",
                }}
                transition="all 0.2s"
              >
                <VStack align="start" spacing={3}>
                  <Avatar name={leader.name} size="md" bg="#dc2626" color="white" />
                  <Heading size="sm" color={textPrimary}>
                    {leader.name}
                  </Heading>
                  <Text fontSize="xs" color={textSecondary}>
                    {leader.role} -- {leader.organisation}
                  </Text>
                  <Badge colorScheme="red" fontSize="2xs">
                    {leader.relevance}
                  </Badge>
                  <List spacing={1} mt={2}>
                    {leader.keyPositions.map((pos, i) => (
                      <ListItem key={i} fontSize="sm" color={textPrimary}>
                        <ListIcon as={ChevronRightIcon} color="#dc2626" />
                        {pos}
                      </ListItem>
                    ))}
                  </List>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>

          <Box textAlign="center" mt={4}>
            <Button
              as={RouterLink}
              to="/energy-research/relationship-map"
              colorScheme="red"
              variant="outline"
            >
              View Relationship Map (AU + Japan)
            </Button>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default AustraliaDecisionMakers;
